import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditTrailAnalysis = ({ auditEntries, loading, onRefresh, onExport, filters, onFilterChange }) => {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getSourceBadgeColor = (source) => {
    switch (source) {
      case 'system':
        return 'bg-blue-100 text-blue-800';
      case 'compliance_report':
        return 'bg-purple-100 text-purple-800';
      case 'approval_workflow':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredEntries = auditEntries?.filter(entry =>
    searchTerm === '' ||
    entry?.actionDescription?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
    entry?.performedBy?.toLowerCase()?.includes(searchTerm?.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3, 4, 5]?.map(i => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col gap-4 mb-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
          <Icon name="FileText" className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 flex-shrink-0" />
          <span className="break-words">Comprehensive Audit Trail Analysis</span>
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="Search" className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search audit logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <select
            value={filters?.dataClassification}
            onChange={(e) => onFilterChange?.({ ...filters, dataClassification: e?.target?.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-full sm:w-auto min-w-[160px]"
          >
            <option value="all">All Classifications</option>
            <option value="public">Public</option>
            <option value="internal">Internal</option>
            <option value="confidential">Confidential</option>
            <option value="restricted">Restricted</option>
          </select>
          <div className="flex gap-2">
            <Button onClick={onExport} variant="outline" size="sm" className="flex items-center gap-2 whitespace-nowrap">
              <Icon name="Download" className="w-4 h-4" />
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button onClick={onRefresh} variant="outline" size="sm" className="flex-shrink-0">
              <Icon name="RefreshCw" className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-bold text-gray-900">{filteredEntries?.length}</p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Total Entries</p>
          </div>
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {filteredEntries?.filter(e => e?.source === 'system')?.length}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">System Actions</p>
          </div>
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {filteredEntries?.filter(e => e?.source === 'compliance_report')?.length}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Compliance Events</p>
          </div>
          <div className="text-center">
            <p className="text-xl sm:text-2xl font-bold text-gray-900">
              {filteredEntries?.filter(e => e?.source === 'approval_workflow')?.length}
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">Approval Actions</p>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {filteredEntries?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="FileText" className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No audit entries found matching your criteria.</p>
          </div>
        ) : (
          filteredEntries?.map(entry => (
            <div
              key={entry?.id}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => setSelectedEntry(entry)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSourceBadgeColor(entry?.source)}`}>
                      {entry?.source?.replace('_', ' ')?.toUpperCase()}
                    </span>
                    <span className="text-sm text-gray-500">
                      {new Date(entry?.performedAt)?.toLocaleString()}
                    </span>
                  </div>
                  <h3 className="text-base font-medium text-gray-900 mb-1">{entry?.actionDescription}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Icon name="User" className="w-4 h-4" />
                      {entry?.performedBy}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Tag" className="w-4 h-4" />
                      {entry?.actionType}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="Database" className="w-4 h-4" />
                      {entry?.entityType}
                    </span>
                  </div>
                </div>
                <Icon name="ChevronRight" className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          ))
        )}
      </div>
      {/* Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setSelectedEntry(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={(e) => e?.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">Audit Entry Details</h3>
                <button onClick={() => setSelectedEntry(null)} className="text-gray-400 hover:text-gray-600">
                  <Icon name="X" className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Action Description</label>
                  <p className="mt-1 text-gray-900">{selectedEntry?.actionDescription}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Performed By</label>
                    <p className="mt-1 text-gray-900">{selectedEntry?.performedBy}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Timestamp</label>
                    <p className="mt-1 text-gray-900">{new Date(selectedEntry?.performedAt)?.toLocaleString()}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Entity Type</label>
                    <p className="mt-1 text-gray-900">{selectedEntry?.entityType}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Action Type</label>
                    <p className="mt-1 text-gray-900">{selectedEntry?.actionType}</p>
                  </div>
                </div>
                {selectedEntry?.changes && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Changes Made</label>
                    <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                      {JSON.stringify(selectedEntry?.changes, null, 2)}
                    </pre>
                  </div>
                )}
                {selectedEntry?.metadata && (
                  <div>
                    <label className="text-sm font-medium text-gray-700">Metadata</label>
                    <pre className="mt-1 p-3 bg-gray-50 rounded-lg text-sm overflow-x-auto">
                      {JSON.stringify(selectedEntry?.metadata, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditTrailAnalysis;